import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft, FiCheck, FiX, FiCalendar, FiMapPin,
  FiBookOpen, FiUsers, FiTrash2, FiCheckCircle, FiXCircle,
  FiLock, FiSave
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { recitativoService } from '../../services/api';
import { exportarContatosWhatsAppHTML } from '../../utils/exportHelpers';
import './DetalhesRecitativo.css';

const TURMA_LABEL = { 1: '1ª turma · 3-6 anos', 2: '2ª turma · 7-11 anos', 3: '3ª turma · 12+ anos' };

const DetalhesRecitativo = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [recitativo, setRecitativo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingIds, setSavingIds] = useState(new Set());
  const [fechando, setFechando] = useState(false);

  const fechado = recitativo?.fechado === true;

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await recitativoService.getById(id);
      setRecitativo(resp.data);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar recitativo');
      navigate('/recitativos');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { carregar(); }, [carregar]);

  const marcarPresente = async (entrega, presente) => {
    const novoValor = entrega.presente === presente ? null : presente;
    setSavingIds((prev) => new Set(prev).add(entrega.id));
    setRecitativo((prev) => ({
      ...prev,
      entregas: prev.entregas.map((e) =>
        e.id === entrega.id ? { ...e, presente: novoValor, marcado_em: novoValor != null ? new Date().toISOString() : null } : e
      ),
    }));
    try {
      await recitativoService.marcarPresenca(recitativo.id, entrega.id, { presente: novoValor });
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar. Tentando reverter…');
      // Em caso de erro, recarrega tudo para sincronizar
      carregar();
    } finally {
      setSavingIds((prev) => {
        const novo = new Set(prev);
        novo.delete(entrega.id);
        return novo;
      });
    }
  };

  const handleFechar = async () => {
    if (!window.confirm('Salvar a frequência e fechar o registro? Depois disso não será mais possível alterar as marcações.')) return;
    setFechando(true);
    try {
      await recitativoService.fechar(id);
      setRecitativo((prev) => ({ ...prev, fechado: true, fechado_em: new Date().toISOString() }));
      toast.success('Frequência salva e registro fechado!');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao fechar recitativo');
    } finally {
      setFechando(false);
    }
  };

  const handleExcluir = async () => {
    if (!window.confirm('Excluir este recitativo? Essa ação não pode ser desfeita.')) return;
    try {
      await recitativoService.excluir(id);
      toast.success('Recitativo excluído');
      navigate('/recitativos');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao excluir');
    }
  };

  const handleReenviarWhatsApp = () => {
    if (!recitativo) return;
    const membros = recitativo.entregas.map((e) => ({
      ...e.membro,
      // O exporter espera estes campos:
      nome_completo: e.membro.nome_completo,
      tipo_pessoa: e.membro.tipo_pessoa,
      idade: e.membro.idade,
      telefone: e.membro.telefone,
      responsavel: e.membro.responsavel ? {
        nome_completo: e.membro.responsavel.nome_completo,
        telefone: e.membro.responsavel.telefone
      } : null,
    }));
    const sequencia = recitativo.entregas.map((e) => ({ capitulo: e.capitulo, verso: e.verso }));
    const recitativoStr = `${recitativo.livro} ${recitativo.capitulo_inicial}:${recitativo.verso_inicial}`;
    exportarContatosWhatsAppHTML(membros, {
      nomeAuxiliar: recitativo.auxiliar?.nome || '',
      localidade: recitativo.localidade,
      recitativo: recitativoStr,
      titulo: `Recitativos ${TURMA_LABEL[recitativo.turma]}`,
      sequencia,
      crianca1aTurmaMesmoVerso: recitativo.turma === 1,
    });
  };

  const stats = useMemo(() => {
    if (!recitativo) return null;
    const total = recitativo.entregas.length;
    const presentes = recitativo.entregas.filter((e) => e.presente === true).length;
    const ausentes = recitativo.entregas.filter((e) => e.presente === false).length;
    const naoMarcadas = total - presentes - ausentes;
    const avaliadas = presentes + ausentes;
    const pct = avaliadas > 0 ? Math.round((presentes / avaliadas) * 100) : null;
    return { total, presentes, ausentes, naoMarcadas, pct };
  }, [recitativo]);

  const fmtData = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return '—'; }
  };

  if (loading) {
    return <div className="dr-loading">Carregando recitativo…</div>;
  }
  if (!recitativo) return null;

  return (
    <div className="dr-page">
      {/* Topo */}
      <header className="dr-top">
        <button
          type="button"
          className="dr-back"
          onClick={() => navigate('/recitativos')}
          aria-label="Voltar para listagem"
        >
          <FiArrowLeft />
        </button>
        <div className="dr-top-info">
          <h1>
            {recitativo.livro} {recitativo.capitulo_inicial}:{recitativo.verso_inicial}
          </h1>
          <p>
            <span className={`dr-badge turma-${recitativo.turma}`}>{TURMA_LABEL[recitativo.turma]}</span>
            <span className={`dr-badge tipo-${recitativo.tipo_pessoa}`}>
              {recitativo.tipo_pessoa === 'irmao' ? 'Irmãos' : 'Irmãs'}
            </span>
          </p>
        </div>
      </header>

      {/* Resumo */}
      <section className="dr-resumo">
        <div className="dr-meta">
          <span><FiCalendar /> {fmtData(recitativo.data_referencia)}</span>
          <span><FiMapPin /> {recitativo.localidade}</span>
          {recitativo.auxiliar?.nome && <span><FiUsers /> {recitativo.auxiliar.nome}</span>}
        </div>

        {stats && (
          <div className="dr-stats">
            <div className="dr-stat-card dr-stat-pres">
              <strong>{stats.presentes}</strong>
              <small>presentes</small>
            </div>
            <div className="dr-stat-card dr-stat-aus">
              <strong>{stats.ausentes}</strong>
              <small>faltaram</small>
            </div>
            {stats.naoMarcadas > 0 && (
              <div className="dr-stat-card dr-stat-pend">
                <strong>{stats.naoMarcadas}</strong>
                <small>sem registro</small>
              </div>
            )}
            <div className={`dr-stat-card dr-stat-pct ${
              stats.pct == null ? 'pct-neutro' :
              stats.pct >= 70 ? 'pct-bom' :
              stats.pct >= 40 ? 'pct-medio' : 'pct-baixo'
            }`}>
              <strong>{stats.pct != null ? `${stats.pct}%` : '—'}</strong>
              <small>frequência</small>
            </div>
          </div>
        )}

        <div className="dr-acoes">
          <button type="button" className="dr-btn-sec" onClick={handleReenviarWhatsApp}>
            <FaWhatsapp /> Reenviar WhatsApp
          </button>
          {!fechado && (
            <button type="button" className="dr-btn-danger" onClick={handleExcluir}>
              <FiTrash2 /> Excluir
            </button>
          )}
        </div>
      </section>

      {fechado ? (
        <div className="dr-fechado-banner">
          <FiLock /> Frequência salva — registro fechado. Não é possível alterar as marcações.
        </div>
      ) : (
        <>
          <div className="dr-instrucoes">
            <FiBookOpen /> Marque quem compareceu na reunião. Toque em <FiCheck className="ic-on" /> ou <FiX className="ic-off" />.
          </div>
          <div className="dr-salvar-wrap">
            <button
              type="button"
              className="dr-btn-salvar"
              onClick={handleFechar}
              disabled={fechando}
            >
              {fechando ? <FiSave className="dr-spin" /> : <FiSave />}
              {fechando ? 'Salvando…' : 'Salvar Frequência'}
            </button>
            <p className="dr-salvar-dica">Após salvar, as marcações não poderão ser alteradas.</p>
          </div>
        </>
      )}

      {/* Lista checklist */}
      <ul className="dr-lista">
        {recitativo.entregas.map((e) => {
          const ehJovem = e.membro.idade != null && e.membro.idade >= 12;
          const status = e.presente === true ? 'presente'
            : e.presente === false ? 'ausente' : 'pendente';
          const salvando = savingIds.has(e.id);
          return (
            <li key={e.id} className={`dr-item dr-item-${status}`}>
              <div className="dr-item-info">
                <span className="dr-item-ordem">{e.ordem}</span>
                <div className="dr-item-dados">
                  <span className="dr-item-nome">{e.membro.nome_completo}</span>
                  <span className="dr-item-meta">
                    <span className="dr-item-ref">
                      {recitativo.livro} {e.capitulo}:{e.verso}
                    </span>
                    {e.membro.idade != null && <span> · {e.membro.idade} anos</span>}
                    {!ehJovem && e.membro.responsavel && (
                      <span> · resp.: {e.membro.responsavel.nome_completo}</span>
                    )}
                  </span>
                </div>
              </div>
              <div className="dr-item-toggle" role="group" aria-label="Marcar presença">
                <button
                  type="button"
                  className={`dr-tg dr-tg-on ${e.presente === true ? 'ativo' : ''}`}
                  onClick={() => !fechado && marcarPresente(e, true)}
                  disabled={salvando || fechado}
                  aria-label="Marcar como presente"
                >
                  <FiCheckCircle />
                  <span>Foi</span>
                </button>
                <button
                  type="button"
                  className={`dr-tg dr-tg-off ${e.presente === false ? 'ativo' : ''}`}
                  onClick={() => !fechado && marcarPresente(e, false)}
                  disabled={salvando || fechado}
                  aria-label="Marcar como ausente"
                >
                  <FiXCircle />
                  <span>Faltou</span>
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default DetalhesRecitativo;
